import { useInView } from '../../hooks/useInView';

interface Reason {
  icon: string;
  title: string;
  description: string;
}

interface WhyUsProps {
  eyebrow?: string;
  headline: string;
  reasons: Reason[];
  image: string;
  imageAlt?: string;
  ctaText?: string;
  ctaHref?: string;
  badge1?: { label: string; sublabel: string };
  badge2?: { value: string; label: string };
  extraParagraph?: string;
}

export default function SectionWhyUs({
  eyebrow = 'Why Ace Diving',
  headline,
  reasons,
  image,
  imageAlt = 'Ace Diving professional instruction',
  ctaText = 'Train With Our Team',
  ctaHref = '#contact',
  badge1 = { label: 'PADI / SSI / SDI', sublabel: 'Multi-Agency Certified' },
  badge2 = { value: '15+', label: 'Years in Miami' },
  extraParagraph,
}: WhyUsProps) {
  const { ref, inView } = useInView();

  return (
    <section id="why-us" className="bg-white py-28 md:py-36 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref as React.RefObject<HTMLDivElement>} className="flex flex-col lg:flex-row gap-16 xl:gap-20 items-center">
          {/* Left */}
          <div
            className={`lg:w-[55%] transition-all duration-700 ${
              inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10 bg-[#00B4D8]" />
              <span className="text-[#00B4D8] text-[11px] font-bold uppercase tracking-[0.25em]">
                {eyebrow}
              </span>
            </div>
            <h2
              className="text-4xl md:text-5xl font-bold text-[#0D1B2A] mb-8 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {headline}
            </h2>

            {extraParagraph && (
              <p className="text-[#57697A] text-lg leading-relaxed mb-10">{extraParagraph}</p>
            )}

            <div className="space-y-6">
              {reasons.map((reason) => (
                <div key={reason.title} className="flex gap-5 group">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#F0FAFE] shrink-0 group-hover:bg-[#00B4D8] transition-colors duration-300">
                    <i
                      className={`${reason.icon} text-[#00B4D8] group-hover:text-white text-lg transition-colors duration-300`}
                    />
                  </div>
                  <div className="pt-0.5">
                    <h3 className="text-[#0D1B2A] font-semibold text-[15px] mb-1 leading-snug">
                      {reason.title}
                    </h3>
                    <p className="text-[#687585] text-sm leading-relaxed">{reason.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <a
              href={ctaHref}
              className="inline-flex items-center gap-2.5 bg-[#0D1B2A] hover:bg-[#162640] text-white font-semibold px-9 py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5 mt-12 whitespace-nowrap cursor-pointer text-[15px]"
            >
              {ctaText}
              <i className="ri-arrow-right-line" />
            </a>
          </div>

          {/* Right: image with floating badges */}
          <div
            className={`lg:w-[45%] w-full transition-all duration-700 delay-200 ${
              inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="relative">
              <div className="w-full h-[540px] rounded-2xl overflow-hidden">
                <img
                  src={image}
                  alt={imageAlt}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#0A1628]/40 via-transparent to-transparent" />
              </div>

              {/* Badge bottom-left */}
              <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl p-4 flex items-center gap-3.5 shadow-lg">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#E8F8FC] shrink-0">
                  <i className="ri-award-fill text-[#00B4D8] text-xl" />
                </div>
                <div>
                  <div
                    className="text-[15px] font-bold text-[#0D1B2A] leading-none"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {badge1.label}
                  </div>
                  <div className="text-[#687585] text-[11px] mt-0.5">{badge1.sublabel}</div>
                </div>
              </div>

              {/* Badge top-right */}
              <div className="absolute -top-5 -right-5 bg-[#0A1628] rounded-2xl px-5 py-4 text-center">
                <div
                  className="text-3xl font-bold text-[#00B4D8] leading-none"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {badge2.value}
                </div>
                <div className="text-white/55 text-[11px] mt-1 whitespace-nowrap">{badge2.label}</div>
              </div>

              {/* Waterfront badge */}
              <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-line text-[#00B4D8] text-sm" />
                </div>
                <span className="text-[#0D1B2A] text-[12px] font-semibold">Pier 5 · Biscayne Bay</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
